import type { CSSProperties, MouseEvent } from 'react';
import { MODULE_REGISTRY } from '../routes/module-registry';
import { navigate, withAppBasePath } from '../nav';

type ModuleKey = keyof typeof MODULE_REGISTRY;

function onClickNav(to: string) {
  return (e: MouseEvent) => {
    // Allow Ctrl+click / middle-click to open new tab natively
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    navigate(to);
  };
}

function isActive(pathname: string, to: string): boolean {
  if (to === '/') return pathname === '/' || pathname === '';
  // Match prefix at /m/{key}/* — handles both /m/key and /m/key/anything
  // PLUS any saved lastUrl variant (which always starts with /m/{key})
  return pathname === to || pathname.startsWith(to + '/');
}

export function Sidebar({
  pathname,
  lastUrlByModule,
}: {
  pathname: string;
  lastUrlByModule: Partial<Record<ModuleKey, string>>;
}) {
  return (
    <aside className="sidebar">
      <div className="brand">Admin Portal</div>
      <nav>
        <a
          href={withAppBasePath('/')}
          onClick={onClickNav('/')}
          className={`nav-item${isActive(pathname, '/') ? ' active' : ''}`}
        >
          Dashboard
        </a>
        <a
          href={withAppBasePath('/chat/web')}
          onClick={onClickNav('/chat/web')}
          className={`nav-item${pathname.startsWith('/chat') ? ' active' : ''}`}
        >
          <span className="dot" style={{ '--module-accent': '#a855f7' } as CSSProperties} />
          AI Console
        </a>
        {Object.entries(MODULE_REGISTRY).map(([key, mod]) => {
          const moduleBase = `/m/${key}`;
          // If we have a saved lastUrl for this module AND it's a valid
          // /m/{key}/... path, navigate there. Otherwise default to the
          // module root.
          const savedUrl = lastUrlByModule[key as ModuleKey];
          const target =
            savedUrl && savedUrl.startsWith(moduleBase) ? savedUrl : moduleBase;
          return (
            <a
              key={key}
              href={withAppBasePath(target)}
              onClick={onClickNav(target)}
              className={`nav-item${isActive(pathname, moduleBase) ? ' active' : ''}`}
              style={mod.accent ? ({ '--module-accent': mod.accent } as CSSProperties) : undefined}
            >
              <span className="dot" />
              {mod.name}
            </a>
          );
        })}
      </nav>
    </aside>
  );
}
